import React, { FC } from 'react';
import { useDragLayer, XYCoord } from 'react-dnd';
import { makeStyles } from '@mui/styles';
import { itemTypes } from './itemTypes';
import { NoteListItemDragPreview } from './dragPreviews/NoteListItemDragPreview';

interface StyleProps {
  initialOffset: XYCoord | null;
  currentOffset: XYCoord | null;
}

interface CustomDragLayerProps {
  dataTestId?: string;
}

const useStyles = makeStyles(() => ({
  root: {
    position: 'fixed',
    pointerEvents: 'none',
    left: 0,
    top: 0,
    zIndex: 100
  },
  preview: {
    transform: ({ currentOffset }: StyleProps) => {
      if (!currentOffset) return undefined;
      const { x, y } = currentOffset;
      return `translate(${x}px, ${y}px)`;
    }
  }
}));

export const CustomDragLayer: FC<CustomDragLayerProps> = ({
  dataTestId
}: CustomDragLayerProps) => {
  const {
    itemType,
    isDragging,
    item,
    initialOffset,
    currentOffset
  } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    itemType: monitor.getItemType(),
    isDragging: monitor.isDragging(),
    initialOffset: monitor.getInitialSourceClientOffset(),
    currentOffset: monitor.getSourceClientOffset()
  }));
  const classes = useStyles({
    initialOffset: initialOffset,
    currentOffset: currentOffset
  });
  function renderItem() {
    switch (itemType) {
      case itemTypes.NOTE_LIST_ITEM:
        return (
          <NoteListItemDragPreview
            note={item.note}
            isItemUncategorized={item.isItemUncategorized}
            isItemScrolled={item.isItemScrolled}
            width={item.width}
            height={item.height}
          />
        );
      default:
        return null;
    }
  }

  if (!isDragging) {
    return null;
  }

  return (
    <div
      data-testid={dataTestId ?? 'custom-drag-layer'}
      className={classes.root}
    >
      <div className={classes.preview}>{renderItem()}</div>
    </div>
  );
};
